import React, { useContext, useEffect } from 'react';
import useTranslate from '~/hooks/useTranslate';
import { useDispatch, useSelector } from 'react-redux';
import { ApiStatus, RootState } from '~/store/types/sharedTypes';
import {
  Button,
  Group,
  H7,
  MaterialIcon,
  Stack,
} from '@qred/components-library';
import { pushToGtmOnboardingAction } from '~/store/actions/gtmActions';
import { getEskatIntegrationUrl } from '~/store/actions/eskatActions';
import { useHistory } from 'react-router';
import constVars from '~/constants/constVars';
import { ValidationContext } from '~/components/hoc/withValidation';

const ConnectToEskat = () => {
  const t = useTranslate();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    onboardingApplication: {
      form: { qCode, applicationUuid, eskatConnected },
    },
    eskat,
    matchedMedia: { mobile },
  } = useSelector((state: RootState) => state);

  const validationContext = useContext(ValidationContext);

  const connectToEskatOnClickHandler = () => {
    if (qCode && applicationUuid) {
      dispatch(
        pushToGtmOnboardingAction({
          actionName: 'eskat_connect_pressed',
        })
      );
      sessionStorage.setItem('eskat-flow-onboarding', applicationUuid);
      dispatch(getEskatIntegrationUrl(qCode));
    }
  };

  useEffect(() => {
    if (!eskatConnected) {
      validationContext.addPropertyToValidationErrors('eskatConnected');
    }

    return () => {
      validationContext.removePropertyFromValidationErrors('eskatConnected');
    };
  }, [eskatConnected]);

  const isTestEnvironment = ['development', 'test'].includes(
    import.meta.env.MODE
  );

  const bypassInTestEnvOnClickHandler = () => {
    if (qCode && applicationUuid) {
      dispatch(
        pushToGtmOnboardingAction({
          actionName: 'eskat_connect_pressed',
        })
      );
      sessionStorage.setItem('eskat-flow-onboarding', applicationUuid);
      history.push(`/${constVars.ROUTE_ESKAT}/redirected`);
    }
  };

  return (
    <Stack mt="xl" spacing="lg" align={mobile ? 'stretch' : 'center'}>
      {eskatConnected ? (
        <Group align="center" spacing={'sm'}>
          <MaterialIcon src="check_circle" />
          <H7>{t('Onboarding.EskatSuccessText')}</H7>
        </Group>
      ) : (
        <Stack spacing="sm">
          <Button
            variant="primary"
            size="md"
            dataCy="connect_to_eskat_button"
            onClick={connectToEskatOnClickHandler}
            disabled={eskatConnected || eskat.apiStatus === ApiStatus.Started}
            isLoading={eskat.apiStatus === ApiStatus.Started}
            fullWidth={mobile}
            mt={5}
          >
            {t('Onboarding.ConnectToEskat')}
          </Button>

          {isTestEnvironment && (
            <Button
              variant="secondary"
              size="md"
              onClick={bypassInTestEnvOnClickHandler}
              disabled={eskatConnected || eskat.apiStatus === ApiStatus.Started}
              isLoading={eskat.apiStatus === ApiStatus.Started}
              fullWidth={mobile}
              mt={5}
            >
              {'Emulate Successful Connection'}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ConnectToEskat;
